import * as React from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useGoogleMaps } from "../../contexts/google_map_context";

interface MapComponentProps {
  center: google.maps.LatLngLiteral;
  zoom: number;
  markerPosition?: google.maps.LatLngLiteral;
  onMarkerPositionChange: (newPosition: google.maps.LatLngLiteral) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({
  center,
  zoom,
  markerPosition,
  onMarkerPositionChange,
}) => {
  const { isLoaded } = useGoogleMaps();
  const mapRef = React.useRef<google.maps.Map | null>(null);
  const markerRef = React.useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const circleRef = React.useRef<google.maps.Circle | null>(null);

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
  };

  const circleOptions: google.maps.CircleOptions = {
    strokeColor: "#2563eb",
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: "#2563eb",
    fillOpacity: 0.35,
    radius: 1000,
  };

  const onLoad = React.useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const createMarkerElement = () => {
    const parser = new DOMParser();
    return parser.parseFromString(
      `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="#2563eb" stroke="white" stroke-width="2"/>
        <path d="M20 12v16M12 20h16" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>`,
      "image/svg+xml",
    ).documentElement;
  };

  React.useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    const { AdvancedMarkerElement } = google.maps.marker;

    if (markerPosition) {
      if (markerRef.current) {
        markerRef.current.position = markerPosition;
      } else {
        markerRef.current = new AdvancedMarkerElement({
          map: mapRef.current,
          position: markerPosition,
          content: createMarkerElement(),
        });
      }
      if (circleRef.current) {
        circleRef.current.setCenter(markerPosition);
      } else {
        circleRef.current = new google.maps.Circle({
          ...circleOptions,
          map: mapRef.current,
          center: markerPosition,
        });
      }
    } else {
      // If no markerPosition, remove existing marker/circle if any
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    }
  }, [isLoaded, markerPosition, circleOptions]);

  const handleMapClick = React.useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const newPosition = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };
        onMarkerPositionChange(newPosition);
      }
    },
    [onMarkerPositionChange],
  );

  const renderMap = () => {
    return (
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={zoom}
        onLoad={onLoad}
        onClick={handleMapClick}
        options={{
          mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID",
          gestureHandling: "cooperative",
        }}
      />
    );
  };

  if (!isLoaded) {
    return <div>Loading maps</div>;
  }

  return renderMap();
};

export default React.memo(MapComponent);
